import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import { useMemo } from 'react';
import EMPTY_SURVEYS_IMG from '@hotjar/shared-assets/images/empty-surveys-new.png';
import EMPTY_FEEDBACK_IMG from '@hotjar/shared-assets/images/empty-if-new.png';
import { tokens } from '@hotjar/hotsauce';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useSurveyTemplatesQuery } from '@hotjar/survey-templates/src/domain/SurveyTemplates/query';
import type { SurveyTemplate } from '@hotjar/survey-templates/src/domain/SurveyTemplates/SurveyTemplate';
import { SurveyTemplatesListItem } from '@hotjar/survey-templates/src/components/SurveyTemplatesList/SurveyTemplatesListItem/SurveyTemplatesListItem';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { useCurrentSiteId } from '@hotjar/site';

const Wrapper = styled.div`
  width: 100%;
  padding: 0 ${tokens.spacer.x4};
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.spacer.x3pt5};
`;

const SurveyWrapper = styled(Wrapper)`
  padding: ${tokens.spacer.x1} ${tokens.spacer.x4};

  & > * {
    border: 1px solid ${tokens.color.ui_border};
    border-radius: ${tokens.radius.l};
    padding: 0 0 ${tokens.spacer.x2};
    width: 304px !important;
    margin: 0 !important;
    overflow: hidden;

    & > img {
      width: 100%;
      max-width: 100%;
      object-fit: cover;
      height: 150px;
      margin-bottom: ${tokens.spacer.x2};
    }

    span {
      padding: 0 ${tokens.spacer.x2};
    }
  }
`;

interface PendingSetupStateProps {
  onButtonClick: () => void;
}

export const PendingSurveysSetupState = ({
  onButtonClick,
}: PendingSetupStateProps): JSX.Element => {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const handleClick = () => {
    trackClick('Create Survey', 'Pending Surveys Setup Empty State', {});
    onButtonClick();
  };
  const currentSiteId = useCurrentSiteId();
  const hasSurveyCreatePermission = usePermission('surveys-create');
  const { data } = useSurveyTemplatesQuery();
  const templates: SurveyTemplate[] =
    useMemo(
      () =>
        data?.survey_templates
          .filter(({ empty_state_highlighted: higlighted }: SurveyTemplate) => higlighted)
          .map((template: SurveyTemplate) => ({
            ...template,
            internal_name: template.empty_state_highlight_title || template.internal_name,
            intro_text: template.empty_state_highlight_description || template.intro_text,
          }))
          .sort((a: SurveyTemplate, b: SurveyTemplate) => {
            const aOrder = a.empty_state_highlight_order || 1;
            const bOrder = b.empty_state_highlight_order || 1;
            return aOrder > bOrder ? 1 : -1;
          }),
      [data?.survey_templates],
    ) || [];

  return (
    <SurveyWrapper>
      {templates.map((template, index) => (
        <SurveyTemplatesListItem
          testIndex={index}
          key={template.entry_id}
          onClick={() => {
            trackClick(
              'Empty states survey template chosen',
              'Pending Surveys Setup Empty State',
              {},
            );
            navigateTo(SURVEYS.CREATE.path, {
              siteId: currentSiteId ?? '',
              templateId: template.survey_id ?? '',
              activeFlowSection: 'name',
              category: encodeURIComponent(template.categories[0]?.name ?? ''),
            });
          }}
          title={template.title}
          description={template.intro_text}
          imageUrl={template.hero_image}
        />
      ))}
      <EmptyState
        img={EMPTY_SURVEYS_IMG}
        title=""
        subtitle=""
        buttonIcon="plus"
        buttonText={t('activation:empty-states.pending-setup.surveys.cta')}
        buttonIsDisabled={!hasSurveyCreatePermission}
        buttonIsDisabledTooltip={t(
          'activation:empty-states.pending-setup.surveys.lacking-permissions',
        )}
        onButtonClick={handleClick}
      />
    </SurveyWrapper>
  );
};

export const PendingFeedbackSetupState = ({
  onButtonClick,
}: PendingSetupStateProps): JSX.Element => {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const handleClick = () => {
    trackClick('Create Feedback', 'Pending Feedback Setup Empty State', {});
    onButtonClick();
  };

  const hasFeedbackCreatePermission = usePermission('feedbacks-create');

  return (
    <Wrapper>
      <EmptyState
        img={EMPTY_FEEDBACK_IMG}
        title={t('activation:empty-states.pending-setup.feedback.title')}
        subtitle={t('activation:empty-states.pending-setup.feedback.subtitle')}
        buttonIcon="plus"
        buttonText={t('activation:empty-states.pending-setup.feedback.cta')}
        buttonIsDisabled={!hasFeedbackCreatePermission}
        buttonIsDisabledTooltip={t(
          'activation:empty-states.pending-setup.feedback.lacking-permissions',
        )}
        onButtonClick={handleClick}
      />
    </Wrapper>
  );
};
